import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Tag,
  Button,
  Space,
  message,
  Modal,
  Descriptions,
  Input,
  Select,
  Avatar,
  Tooltip,
  Popconfirm,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  UserOutlined,
  GiftOutlined,
  SearchOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import apiClient from '../services/apiConfig';
import type { ColumnsType } from 'antd/es/table';

const { Search } = Input;
const { Option } = Select;

interface Redemption {
  id: number;
  redemption_code: string;
  status: 'pending' | 'completed' | 'cancelled';
  created_at: string;
  redeemed_at?: string;
  points_used: number;
  notes?: string;
  user: {
    id: number;
    name: string;
    last_name?: string;
    email: string;
    category?: string;
  };
  reward: {
    id: number;
    name: string;
    description?: string;
    points_cost: number;
    image?: string;
  } | null;
}

const RedemptionsManagementPage: React.FC = () => {
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRedemption, setSelectedRedemption] = useState<Redemption | null>(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    loadRedemptions();
  }, []);

  const loadRedemptions = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/rewards/redemptions');
      console.log('API Response:', response.data);
      if (response.data.success) {
        setRedemptions(response.data.data);
      } else {
        console.error('API returned success=false:', response.data);
        message.error(response.data.message || 'Error al cargar los canjes');
      }
    } catch (error: any) {
      console.error('Error loading redemptions:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || 'Error al cargar los canjes';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (code: string) => {
    try {
      await apiClient.post(`/rewards/redemptions/confirm/${code}`);
      message.success('Canje confirmado exitosamente');
      loadRedemptions();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error al confirmar el canje';
      message.error(errorMessage);
    }
  };

  const handleCancel = async (code: string) => {
    try {
      await apiClient.post(`/rewards/redemptions/cancel/${code}`);
      message.success('Canje cancelado y puntos devueltos');
      loadRedemptions();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error al cancelar el canje';
      message.error(errorMessage);
    }
  };

  const showDetails = (redemption: Redemption) => {
    setSelectedRedemption(redemption);
    setDetailsModalVisible(true);
  };

  const getStatusTag = (status: string) => {
    const statusConfig = {
      pending: { color: 'warning', text: 'Pendiente' },
      completed: { color: 'success', text: 'Completado' },
      cancelled: { color: 'error', text: 'Cancelado' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const columns: ColumnsType<Redemption> = [
    {
      title: 'Código',
      dataIndex: 'redemption_code',
      key: 'redemption_code',
      width: 180,
      render: (code) => (
        <span style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: 600 }}>
          {code}
        </span>
      ),
      filteredValue: searchText ? [searchText] : null,
      onFilter: (value, record) => {
        const searchValue = (value as string).toLowerCase();
        const rewardName = record.reward?.name || 'Premio no disponible';
        return (
          record.redemption_code.toLowerCase().includes(searchValue) ||
          record.user.name.toLowerCase().includes(searchValue) ||
          record.user.email.toLowerCase().includes(searchValue) ||
          rewardName.toLowerCase().includes(searchValue)
        );
      },
    },
    {
      title: 'Usuario',
      key: 'user',
      width: 250,
      render: (_, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} size="small" />
          <div>
            <div style={{ fontWeight: 500 }}>
              {record.user.name} {record.user.last_name || ''}
            </div>
            <div style={{ fontSize: '12px', color: '#888' }}>
              {record.user.email}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Premio',
      key: 'reward',
      width: 300,
      render: (_, record) => {
        const rewardName = record.reward?.name || 'Premio no disponible';
        const rewardDescription = record.reward?.description;

        return (
          <div>
            <div style={{ fontWeight: 500 }}>{rewardName}</div>
            {rewardDescription && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                {rewardDescription.substring(0, 60)}
                {rewardDescription.length > 60 ? '...' : ''}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Puntos',
      dataIndex: 'points_used',
      key: 'points_used',
      width: 100,
      align: 'center',
      render: (points) => (
        <Tag color="blue" style={{ fontSize: '13px' }}>
          {points} pts
        </Tag>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      align: 'center',
      render: (status) => getStatusTag(status),
      filters: [
        { text: 'Pendiente', value: 'pending' },
        { text: 'Completado', value: 'completed' },
        { text: 'Cancelado', value: 'cancelled' },
      ],
      filteredValue: statusFilter === 'all' ? null : [statusFilter],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Fecha',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => formatDate(date),
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Ver detalles">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => showDetails(record)}
            />
          </Tooltip>

          {record.status === 'pending' && (
            <>
              <Popconfirm
                title="¿Confirmar este canje?"
                description="El estado cambiará a completado"
                onConfirm={() => handleConfirm(record.redemption_code)}
                okText="Sí"
                cancelText="No"
              >
                <Button
                  type="primary"
                  size="small"
                  icon={<CheckCircleOutlined />}
                  style={{ background: '#52c41a', borderColor: '#52c41a' }}
                >
                  Confirmar
                </Button>
              </Popconfirm>

              <Popconfirm
                title="¿Cancelar este canje?"
                description="Los puntos serán devueltos al usuario"
                onConfirm={() => handleCancel(record.redemption_code)}
                okText="Sí"
                cancelText="No"
              >
                <Button
                  danger
                  size="small"
                  icon={<CloseCircleOutlined />}
                >
                  Cancelar
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  const filteredRedemptions = redemptions;

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <GiftOutlined style={{ fontSize: '20px' }} />
            <span style={{ fontSize: '20px', fontWeight: 600 }}>
              Gestión de Canjes de Premios
            </span>
          </Space>
        }
        extra={
          <Space>
            <Select
              style={{ width: 150 }}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="Filtrar por estado"
            >
              <Option value="all">Todos los estados</Option>
              <Option value="pending">Pendientes</Option>
              <Option value="completed">Completados</Option>
              <Option value="cancelled">Cancelados</Option>
            </Select>

            <Search
              placeholder="Buscar por código, usuario o premio"
              allowClear
              style={{ width: 300 }}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
            />

            <Button
              type="default"
              icon={<ReloadOutlined />}
              onClick={loadRedemptions}
              loading={loading}
            >
              Actualizar
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={filteredRedemptions}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} canjes`,
          }}
        />
      </Card>

      {/* Modal de detalles */}
      <Modal
        title="Detalles del Canje"
        open={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailsModalVisible(false)}>
            Cerrar
          </Button>,
        ]}
        width={700}
      >
        {selectedRedemption && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Código de Canje">
              <span style={{ fontFamily: 'monospace', fontSize: '16px', fontWeight: 600 }}>
                {selectedRedemption.redemption_code}
              </span>
            </Descriptions.Item>

            <Descriptions.Item label="Estado">
              {getStatusTag(selectedRedemption.status)}
            </Descriptions.Item>

            <Descriptions.Item label="Usuario">
              <Space>
                <Avatar icon={<UserOutlined />} />
                <div>
                  <div style={{ fontWeight: 500 }}>
                    {selectedRedemption.user.name} {selectedRedemption.user.last_name || ''}
                  </div>
                  <div style={{ fontSize: '13px', color: '#666' }}>
                    {selectedRedemption.user.email}
                  </div>
                  {selectedRedemption.user.category && (
                    <Tag style={{ marginTop: 4 }}>
                      {selectedRedemption.user.category.toUpperCase()}
                    </Tag>
                  )}
                </div>
              </Space>
            </Descriptions.Item>

            <Descriptions.Item label="Premio">
              <div>
                <div style={{ fontWeight: 500, fontSize: '15px' }}>
                  {selectedRedemption.reward?.name || 'Premio no disponible'}
                </div>
                {selectedRedemption.reward?.description && (
                  <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                    {selectedRedemption.reward.description}
                  </div>
                )}
              </div>
            </Descriptions.Item>

            <Descriptions.Item label="Puntos Utilizados">
              <Tag color="blue" style={{ fontSize: '16px', padding: '6px 14px' }}>
                {selectedRedemption.points_used} puntos
              </Tag>
            </Descriptions.Item>

            <Descriptions.Item label="Fecha de Solicitud">
              {formatDate(selectedRedemption.created_at)}
            </Descriptions.Item>

            {selectedRedemption.redeemed_at && (
              <Descriptions.Item label="Fecha de Canje">
                {formatDate(selectedRedemption.redeemed_at)}
              </Descriptions.Item>
            )}

            {selectedRedemption.notes && (
              <Descriptions.Item label="Notas">
                {selectedRedemption.notes}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default RedemptionsManagementPage;
